// Реестр несоответствий (модуль quality-records): список с фильтром по статусу
// и заказу, карточка с решением, уведомлением заказчика, закрытием и
// корректирующими действиями.  Чтение — manufacturing_order.read, решения,
// закрытие и открытие КД — quality.manage.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { NcStatus, useQualityError } from "@/components/OrderNonconformities";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { qualityApi, type Nonconformity, type NonconformityDisposition } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useStageNames } from "@/lib/stages";
import { CaStatus } from "@/pages/admin/CorrectiveActions";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";
const DISPOSITIONS: NonconformityDisposition[] = ["REWORK", "RECLASSIFY", "CONCESSION", "SCRAP"];

export default function Nonconformities() {
  const { t } = useTranslation();
  const stageName = useStageNames();
  const [params, setParams] = useSearchParams();
  const orderId = params.get("order") ?? undefined;
  const [status, setStatus] = useState<"OPEN" | "CLOSED" | "">("OPEN");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["quality", "nonconformities", { status, orderId, page }],
    queryFn: () =>
      qualityApi.nonconformities({
        status: status || undefined,
        manufacturing_order_id: orderId,
        page,
        page_size: 50,
      }),
  });
  const selected = list.data?.items.find((n) => n.id === selectedId) ?? null;

  return (
    <>
      <PageHeader eyebrow={t("quality.eyebrow")} title={t("quality.nc.title")} description={t("quality.nc.description")} />

      <div className="card p-3 mt-3 flex flex-wrap items-center gap-2">
        {(["OPEN", "CLOSED", ""] as const).map((s) => (
          <button
            key={s || "all"}
            className={`btn btn-sm ${status === s ? "btn-primary" : ""}`}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {s ? t(`quality.status.${s}`) : t("quality.nc.filterAll")}
          </button>
        ))}
        {orderId && (
          <button className="btn btn-sm ml-auto" onClick={() => setParams({})}>
            {t("quality.nc.orderFilter")} ✕
          </button>
        )}
      </div>

      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}
      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">{t("quality.nc.empty")}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-4 mt-4 items-start">
        {list.data && list.data.items.length > 0 && (
          <section className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colNumber")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colOrder")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colStage")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colDescription")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colStatus")}</th>
                  <th className="px-3 py-2 text-left">{t("quality.nc.colOpened")}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((n) => (
                  <tr
                    key={n.id}
                    className={`border-t border-border cursor-pointer hover:bg-surface ${n.id === selectedId ? "bg-surface" : ""}`}
                    onClick={() => setSelectedId(n.id)}
                  >
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{n.number}</td>
                    <td className="px-3 py-2 font-mono text-xs">{n.batch_number ?? n.manufacturing_order_code}</td>
                    <td className="px-3 py-2">{n.stage_code ? stageName.byCode(n.stage_code) : "—"}</td>
                    <td className="px-3 py-2 max-w-sm truncate">{n.description}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <NcStatus n={n} />
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted whitespace-nowrap">{formatDateTime(n.opened_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {selected ? (
          <NonconformityCard key={selected.id} n={selected} />
        ) : (
          list.data &&
          list.data.items.length > 0 && <div className="card p-6 text-sm text-text-muted">{t("quality.nc.select")}</div>
        )}
      </div>

      {list.data && list.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", {
              page: list.data.pagination.page,
              pages: list.data.pagination.pages,
              total: list.data.pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ←
            </button>
            <button className="btn" disabled={page >= list.data.pagination.pages} onClick={() => setPage((p) => p + 1)}>
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function NonconformityCard({ n }: { n: Nonconformity }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const stageName = useStageNames();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("quality.manage") && n.status === "OPEN";

  const [disposition, setDisposition] = useState<NonconformityDisposition | "">(n.disposition ?? "");
  const [note, setNote] = useState(n.disposition_note ?? "");
  const [notice, setNotice] = useState(n.customer_notice ?? "");
  const [comment, setComment] = useState("");

  const done = () => qc.invalidateQueries({ queryKey: ["quality", "nonconformities"] });
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof qualityApi.updateNonconformity>[1]) =>
      qualityApi.updateNonconformity(n.id, patch),
    onSuccess: done,
  });
  const close = useMutation({
    mutationFn: () => qualityApi.closeNonconformity(n.id, comment.trim() || undefined),
    onSuccess: done,
  });
  const err = update.error ?? close.error;

  const who = (name: string | null, role: string | null, at: string | null) =>
    at ? `${name ?? "—"}${role ? ` (${role})` : ""} · ${formatDateTime(at)}` : "—";

  return (
    <section className="card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-mono">{n.number}</h2>
          <Link to={`/manufacturing-orders/${n.manufacturing_order_id}`} className="text-sm text-accent-strong hover:underline">
            {n.batch_number ?? n.manufacturing_order_code}
          </Link>
        </div>
        <NcStatus n={n} />
      </div>

      <dl className="text-sm space-y-1.5">
        <Row label={t("quality.nc.stage")} value={n.stage_code ? stageName.byCode(n.stage_code) : null} />
        <Row label={t("quality.nc.reason")} value={n.reason_code} />
        <Row label={t("quality.nc.description")} value={n.description} />
        <Row label={t("quality.nc.quantity")} value={n.quantity != null ? `${n.quantity} ${n.unit ?? ""}` : null} />
        <Row label={t("quality.nc.opened")} value={who(n.opener_name, n.opener_role, n.opened_at)} />
        <Row
          label={t("quality.nc.decision")}
          value={n.disposition ? `${t(`quality.disposition.${n.disposition}`)} · ${who(n.decider_name, n.decider_role, n.decided_at)}` : null}
        />
        {n.disposition_note && <Row label={t("quality.nc.dispositionNote")} value={n.disposition_note} />}
        <Row
          label={t("quality.nc.notified")}
          value={n.customer_notified_at ? `${formatDateTime(n.customer_notified_at)}${n.customer_notice ? ` · ${n.customer_notice}` : ""}` : null}
        />
        {n.closed_at && <Row label={t("quality.nc.closed")} value={formatDateTime(n.closed_at)} />}
      </dl>

      <LinkedCorrectiveActions ncId={n.id} />

      {err && <ErrorBanner onDismiss={() => {
            update.reset();
            close.reset();
          }}>{errText(err)}</ErrorBanner>}

      {canManage && (
        <div className="space-y-4 border-t border-border pt-4">
          <div className="space-y-2">
            <select
              className={INPUT_CLS}
              value={disposition}
              onChange={(e) => setDisposition(e.target.value as NonconformityDisposition | "")}
            >
              <option value="">{t("quality.nc.chooseDisposition")}</option>
              {DISPOSITIONS.map((d) => (
                <option key={d} value={d}>
                  {t(`quality.disposition.${d}`)}
                </option>
              ))}
            </select>
            <textarea
              className={INPUT_CLS}
              rows={2}
              placeholder={t("quality.nc.dispositionNote")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              className="btn btn-sm btn-primary"
              disabled={!disposition || update.isPending}
              onClick={() =>
                disposition && update.mutate({ disposition, disposition_note: note.trim() || null })
              }
            >
              {t("quality.nc.saveDecision")}
            </button>
          </div>

          <div className="space-y-2">
            <textarea
              className={INPUT_CLS}
              rows={2}
              placeholder={t("quality.nc.customerNotice")}
              value={notice}
              onChange={(e) => setNotice(e.target.value)}
            />
            <button
              className="btn btn-sm"
              disabled={update.isPending}
              onClick={() => update.mutate({ customer_notified: true, customer_notice: notice.trim() || null })}
            >
              {t("quality.nc.markNotified")}
            </button>
          </div>

          <button className="btn btn-sm" disabled={update.isPending} onClick={() => update.mutate({ quarantine: !n.quarantine })}>
            {n.quarantine ? t("quality.nc.quarantineOff") : t("quality.nc.quarantineOn")}
          </button>

          <div className="space-y-2 border-t border-border pt-4">
            {n.disposition === "CONCESSION" && !n.customer_notified_at && (
              <p className="text-xs text-text-muted">{t("quality.nc.concessionHint")}</p>
            )}
            <input
              className={INPUT_CLS}
              placeholder={t("quality.nc.closeComment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button className="btn btn-sm btn-primary" disabled={close.isPending} onClick={() => close.mutate()}>
              {t("quality.nc.close")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** КД по несоответствию; новое открывается и по закрытому — анализ идёт после решения. */
function LinkedCorrectiveActions({ ncId }: { ncId: string }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const linked = useQuery({
    queryKey: ["quality", "corrective-actions", "list", { nonconformity_id: ncId }],
    queryFn: () => qualityApi.correctiveActions({ nonconformity_id: ncId, page_size: 100 }),
  });
  const items = linked.data?.items ?? [];
  return (
    <div className="space-y-2 border-t border-border pt-4">
      <h3 className="text-sm font-semibold">{t("quality.ca.linkedTitle")}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">{linked.isLoading ? t("common.loading") : t("quality.ca.noneLinked")}</p>
      ) : (
        <ul className="text-sm space-y-1">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2">
              <Link to={`/admin/corrective-actions?id=${a.id}`} className="text-accent-strong hover:underline truncate">
                <span className="font-mono">{a.number}</span> · {a.title}
              </Link>
              <CaStatus a={a} />
            </li>
          ))}
        </ul>
      )}
      {hasPermission("quality.manage") && (
        <Link to={`/admin/corrective-actions?nc=${ncId}`} className="btn btn-sm">
          {t("quality.ca.openFromNc")}
        </Link>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="grid grid-cols-[140px,1fr] gap-2">
      <dt className="text-text-muted">{label}</dt>
      <dd className="break-words">{value ?? "—"}</dd>
    </div>
  );
}
